import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { useCampaignFiles } from '@/app/module/campaigns/component/campaign-files-provider'
import { ERROR_BULK_FILES_UPLOAD_SERVER } from '@/app/module/files/definitions'
import { UploadFilesPayload } from '@/app/module/files/hooks/use-upload-files'
import { createNotification } from '@/app/module/notifications/store/actions'
import { selectOrgId } from '@/app/module/user/store/selectors'
import { esApi, getErrorMessage } from '@/app/store/api'
import { useAppDispatch } from '@/app/store/redux-hooks'
import { FilesType } from '@/app/types'

export const useUploadCampaignFiles = () => {
  const dispatch = useAppDispatch()
  const { setFiles } = useCampaignFiles()
  const orgId = useSelector(selectOrgId)
  const [upload, res] = esApi.endpoints.uploadFile.useMutation()

  const uploadFiles = useCallback(
    async ({ files, filter, video }: UploadFilesPayload) => {
      try {
        const uploaded = await upload({
          orgId: orgId.toString(),
          category: 'uploads',
          filter,
          video,
          body: { upload: files },
        }).unwrap()
        dispatch(
          createNotification({
            'upload-files-success': {
              type: 'success',
              message: 'Files uploaded successfully.',
            },
          }),
        )
        const uploadedFiles = uploaded.files.reduce<FilesType>(
          (acc, file) => ({
            ...acc,
            [file.filename]: {
              ...file,
              url: '', // don't set url and let it be fetched when needed.
            },
          }),
          {},
        )
        setFiles(uploadedFiles)
        return uploaded
      } catch (error) {
        dispatch(
          createNotification({
            'upload-files-failure': {
              type: 'error',
              message: getErrorMessage(error, ERROR_BULK_FILES_UPLOAD_SERVER),
            },
          }),
        )
        return undefined
      }
    },
    [orgId, dispatch, setFiles, upload],
  )

  return [uploadFiles, res] as const
}
